import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useAuthStore } from '@/store/auth.store'
import { expenseClaimService } from '@/services/expenseClaimService'
import { inboxTasksService } from '@/services/inboxTasksService'
import type { ExpenseClaimListItem, ExpenseClaimListResponse } from '@/api/types'
import type { ApprovalStatusItem } from '@/api/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

const APPROVAL_STATUS_LABELS: Record<number, string> = {
  434: 'Pending',
  435: 'Approved',
  436: 'Rejected',
}

export const ExpenseClaimList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [rows, setRows] = useState<ExpenseClaimListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  // Keep all columns visible (toolbar buttons removed)
  const columnVisibility: Record<string, boolean> = {
    claimDate: true,
    amount: true,
    detail: true,
    status: true,
    action: true,
  }

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const list = await inboxTasksService.getApprovalStatuses()
        setStatuses(list || [])
      } catch {
        setStatuses([])
      }
    }
    loadStatuses()
  }, [])

  const resolveStatusName = (row: ExpenseClaimListItem): string => {
    const statusCode = Number(row.status ?? 0)
    if (statusCode && APPROVAL_STATUS_LABELS[statusCode]) return APPROVAL_STATUS_LABELS[statusCode]
    if (row.statusName && row.statusName.trim().length > 0) return row.statusName
    if (!statusCode) return '—'
    const match = statuses.find((s) => Number(s.lookupCodeId ?? s.id) === statusCode)
    const text = match?.lookupNameEn || match?.lookupNameAr
    return text?.trim() ? text : String(statusCode)
  }

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res: ExpenseClaimListResponse = await expenseClaimService.getAll(branchId, pageNumber, pageSize)
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
      } catch (e: any) {
        const msg = (e?.response?.data?.message || e?.message || 'Failed to load expense claims').toString()
        const isNoData = /not found|no data|no records|no\s+.*\s+found/i.test(msg)
        setRows([])
        setTotalRecords(0)
        if (!isNoData) toast.error(msg)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize])

  const filteredRows = useMemo(() => {
    let list = rows
    if (statusFilter !== 'all') {
      const statusLower = statusFilter.toLowerCase()
      list = list.filter((r) => resolveStatusName(r).toLowerCase().includes(statusLower))
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (r) =>
          String(r.id).toLowerCase().includes(q) ||
          (r.claimDate ?? '').toLowerCase().includes(q) ||
          String(r.amount ?? '').toLowerCase().includes(q) ||
          (r.detail ?? '').toLowerCase().includes(q) ||
          resolveStatusName(r).toLowerCase().includes(q) ||
          (r.approved ?? '').toLowerCase().includes(q)
      )
    }
    return list
  }, [rows, statusFilter, searchQuery, statuses])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return String(dateStr).slice(0, 10)
    }
  }

  const statusBadge = (row: ExpenseClaimListItem) => {
    const text = resolveStatusName(row)
    const isApproved = text.toLowerCase() === 'approved'
    const isPending = text.toLowerCase() === 'pending'
    return (
      <Badge variant={isApproved ? 'default' : isPending ? 'secondary' : 'outline'} className="text-xs">
        {text}
      </Badge>
    )
  }

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete', 'Are you sure you want to delete this expense claim?')
    if (!confirmed) return
    try {
      await expenseClaimService.delete(id)
      await showSuccess('Deleted', 'Expense claim deleted successfully.')
      if (branchId) {
        const res = await expenseClaimService.getAll(branchId, pageNumber, pageSize)
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
      }
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message ?? e?.message ?? 'Failed to delete.')
    }
  }

  // Toolbar export/utility handlers removed (buttons removed as requested)

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Expense Claim Request" description="Expense Claim Detail" />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Expense Claim Request"
        description="Expense Claim Detail"
        action={{
          label: 'Add Request',
          onClick: () => navigate('/self-services/expense-claims/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="space-y-1">
                <label className="text-sm font-medium">Status *</label>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
              {/* Toolbar actions removed (Column Visibility / CSV / Copy / Print) */}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {columnVisibility.claimDate && (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Claim Date</TableHead>
                    )}
                    {columnVisibility.amount && (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Amount</TableHead>
                    )}
                    {columnVisibility.detail && (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Detail</TableHead>
                    )}
                    {columnVisibility.status && (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Status</TableHead>
                    )}
                    {columnVisibility.action && (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-28">Action</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          [
                            columnVisibility.claimDate,
                            columnVisibility.amount,
                            columnVisibility.detail,
                            columnVisibility.status,
                            columnVisibility.action,
                          ].filter(Boolean).length || 1
                        }
                        className="text-center py-12 text-muted-foreground"
                      >
                        No data available in table
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id} className="border-b hover:bg-primary/5">
                        {columnVisibility.claimDate && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.claimDate)}</TableCell>
                        )}
                        {columnVisibility.amount && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.amount ?? '—'}</TableCell>
                        )}
                        {columnVisibility.detail && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[220px] truncate" title={row.detail ?? ''}>
                            {row.detail ?? '—'}
                          </TableCell>
                        )}
                        {columnVisibility.status && (
                          <TableCell className="px-4 py-2 text-center align-middle">{statusBadge(row)}</TableCell>
                        )}
                        {columnVisibility.action && (
                          <TableCell className="px-4 py-2 text-center">
                            <div className="inline-flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-primary hover:bg-primary/10"
                                onClick={() => navigate(`/self-services/expense-claims/${row.id}`)}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                onClick={() => handleDelete(row.id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, totalRecords)} of{' '}
                  {totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
